<template>
  <transition name="modal-fade">
    <div
      v-if="isVisible"
      class="free-diamond-overlay"
      @click.self="handleOverlayClick"
    >
      <div class="free-diamond-modal-content">
        <!-- Close Button -->
        <button class="close-btn" :disabled="claiming" @click="handleClose">
          <feather-icon icon="XIcon" size="20" />
        </button>

        <!-- Header -->
        <div class="modal-header-section">
          <div class="diamond-icon">💎</div>
          <h3 class="modal-title">รับเพชรฟรี</h3>
          <p class="modal-subtitle">รับเพชรฟรีทุกวัน!</p>
        </div>

        <!-- Cannot Claim State (Countdown) -->
        <div
          v-if="!diamondData.canClaim && !showSuccess"
          class="countdown-state"
        >
          <p class="countdown-label">รับเพชรได้อีกครั้งใน</p>
          <div class="countdown-display">
            <div class="time-unit">
              <span class="time-value">{{ formattedTime.hours }}</span>
              <span class="time-label">ชั่วโมง</span>
            </div>
            <span class="time-separator">:</span>
            <div class="time-unit">
              <span class="time-value">{{ formattedTime.minutes }}</span>
              <span class="time-label">นาที</span>
            </div>
            <span class="time-separator">:</span>
            <div class="time-unit">
              <span class="time-value">{{ formattedTime.seconds }}</span>
              <span class="time-label">วินาที</span>
            </div>
          </div>
          <p class="countdown-message">
            กรุณารอจนกว่าจะถึงเวลารับเพชรครั้งต่อไป
          </p>
        </div>

        <!-- Can Claim State -->
        <div
          v-else-if="diamondData.canClaim && !showSuccess"
          class="claim-state"
        >
          <div class="diamonds-display">
            <span class="diamonds-icon">💎</span>
            <span class="diamonds-amount">{{
              diamondData.diamondsAvailable
            }}</span>
            <span class="diamonds-label">เพชร</span>
          </div>
          <p class="claim-message">คุณสามารถรับเพชรฟรีได้แล้ว!</p>
          <b-button
            class="claim-btn"
            :disabled="claiming"
            @click="claimDiamond"
          >
            <b-spinner v-if="claiming" small class="mr-1" />
            <span v-if="!claiming">รับเพชรฟรี</span>
            <span v-else>กำลังดำเนินการ...</span>
          </b-button>
        </div>

        <!-- Success State -->
        <transition name="scale-fade">
          <div v-if="showSuccess" class="success-state">
            <div class="confetti-container">
              <div
                v-for="i in 50"
                :key="i"
                class="confetti"
                :style="getConfettiStyle(i)"
              />
            </div>
            <div class="success-content">
              <div class="success-icon">🎉</div>
              <h4 class="success-title">ยินดีด้วย!</h4>
              <p class="success-message">
                คุณได้รับ <strong>{{ claimedAmount }}</strong> เพชรแล้ว!
              </p>
              <div class="success-diamond-badge">💎 +{{ claimedAmount }}</div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </transition>
</template>

<script>
import { BButton, BSpinner } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'FreeDiamondModal',
  components: {
    BButton,
    BSpinner
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    diamondData: {
      type: Object,
      default: () => ({
        canClaim: false,
        diamondsAvailable: 0,
        hoursRemaining: 0,
        minutesRemaining: 0,
        secondsRemaining: 0,
        nextClaimTime: null
      })
    }
  },
  data() {
    return {
      isVisible: false,
      claiming: false,
      showSuccess: false,
      claimedAmount: 0,
      // Local countdown (initialized from props)
      hoursRemaining: 0,
      minutesRemaining: 0,
      secondsRemaining: 0,
      countdownInterval: null
    }
  },
  computed: {
    formattedTime() {
      const h = String(this.hoursRemaining).padStart(2, '0')
      const m = String(this.minutesRemaining).padStart(2, '0')
      const s = String(this.secondsRemaining).padStart(2, '0')
      return { hours: h, minutes: m, seconds: s }
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.isVisible = newVal
        if (newVal) {
          // Initialize local countdown from props
          this.hoursRemaining = this.diamondData.hoursRemaining || 0
          this.minutesRemaining = this.diamondData.minutesRemaining || 0
          this.secondsRemaining = this.diamondData.secondsRemaining || 0
          if (!this.diamondData.canClaim) {
            this.startCountdown()
          }
        } else {
          this.resetState()
        }
      },
      immediate: true
    }
  },
  beforeDestroy() {
    this.clearCountdownInterval()
  },
  methods: {
    async claimDiamond() {
      if (!this.diamondData.canClaim || this.claiming) return

      this.claiming = true
      try {
        const response = await this.$http.post(
          '/member/diamond/claim-daily-free'
        )
        if (response.data.success) {
          this.claimedAmount = this.diamondData.diamondsAvailable
          this.showSuccess = true
          this.showSuccessToast(`ได้รับ ${this.claimedAmount} เพชรแล้ว!`)

          // Refresh balance
          if (this.$eventBus) {
            this.$eventBus.$emit('refreshBalance')
          }

          setTimeout(() => {
            this.$emit('input', false)
            this.$emit('claimed', this.claimedAmount)
          }, 2500)
        } else {
          this.showErrorToast(response.data.message || 'ไม่สามารถรับเพชรได้')
          // Request parent to refresh eligibility data
          this.$emit('refresh')
        }
      } catch (error) {
        this.showErrorToast('เกิดข้อผิดพลาด กรุณาลองใหม่')
      } finally {
        this.claiming = false
      }
    },

    startCountdown() {
      this.clearCountdownInterval()

      this.countdownInterval = setInterval(() => {
        if (this.secondsRemaining > 0) {
          this.secondsRemaining -= 1
        } else if (this.minutesRemaining > 0) {
          this.minutesRemaining -= 1
          this.secondsRemaining = 59
        } else if (this.hoursRemaining > 0) {
          this.hoursRemaining -= 1
          this.minutesRemaining = 59
          this.secondsRemaining = 59
        } else {
          // Countdown complete - request parent to refresh
          this.clearCountdownInterval()
          this.$emit('refresh')
        }
      }, 1000)
    },

    clearCountdownInterval() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
    },

    showSuccessToast(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'สำเร็จ!',
          text: message,
          icon: 'CheckIcon',
          variant: 'success'
        }
      })
    },

    showErrorToast(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'เกิดข้อผิดพลาด',
          text: message,
          icon: 'AlertTriangleIcon',
          variant: 'danger'
        }
      })
    },

    handleClose() {
      if (!this.claiming) {
        this.$emit('input', false)
      }
    },

    handleOverlayClick() {
      if (!this.claiming) {
        this.$emit('input', false)
      }
    },

    resetState() {
      this.clearCountdownInterval()
      setTimeout(() => {
        this.claiming = false
        this.showSuccess = false
        this.claimedAmount = 0
        this.hoursRemaining = 0
        this.minutesRemaining = 0
        this.secondsRemaining = 0
      }, 300)
    },

    getConfettiStyle(index) {
      const colors = [
        '#00bfff',
        '#00d4ff',
        '#00ffff',
        '#7dd3fc',
        '#38bdf8',
        '#0ea5e9',
        '#d4af37'
      ]
      const color = colors[index % colors.length]
      const left = Math.random() * 100
      const delay = Math.random() * 3
      const duration = 2 + Math.random() * 2
      const size = 6 + Math.random() * 6

      return {
        left: `${left}%`,
        backgroundColor: color,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
        width: `${size}px`,
        height: `${size}px`
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.free-diamond-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 16px;
}

.free-diamond-modal-content {
  position: relative;
  padding: 24px 16px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 20px;
  border: 2px solid rgba(0, 191, 255, 0.3);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  width: 100%;
  max-width: 360px;
  text-align: center;

  @media (min-width: 768px) {
    padding: 32px 24px;
    max-width: 400px;
  }
}

.close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Header
.modal-header-section {
  margin-bottom: 24px;
}

.diamond-icon {
  font-size: 64px;
  margin-bottom: 12px;
  animation: float 2s ease-in-out infinite;
  filter: drop-shadow(0 0 20px rgba(0, 191, 255, 0.5));

  @media (min-width: 768px) {
    font-size: 72px;
  }
}

.modal-title {
  font-size: 24px;
  font-weight: bold;
  color: #00bfff;
  margin: 0 0 8px 0;

  @media (min-width: 768px) {
    font-size: 28px;
  }
}

.modal-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
}

// Countdown State
.countdown-state {
  padding: 20px 0;
}

.countdown-label {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 16px;
}

.countdown-display {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
}

.time-unit {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgba(0, 191, 255, 0.15);
  border: 1px solid rgba(0, 191, 255, 0.3);
  border-radius: 12px;
  padding: 12px 16px;
  min-width: 70px;
}

.time-value {
  font-size: 28px;
  font-weight: bold;
  color: #00bfff;
  line-height: 1;

  @media (min-width: 768px) {
    font-size: 32px;
  }
}

.time-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 4px;
}

.time-separator {
  font-size: 24px;
  font-weight: bold;
  color: #00bfff;
}

.countdown-message {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
}

// Claim State
.claim-state {
  padding: 20px 0;
}

.diamonds-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 16px 24px;
  background: linear-gradient(
    135deg,
    rgba(0, 191, 255, 0.2),
    rgba(0, 191, 255, 0.1)
  );
  border: 1px solid rgba(0, 191, 255, 0.4);
  border-radius: 16px;
  animation: pulse-glow 2s ease-in-out infinite;
}

.diamonds-icon {
  font-size: 32px;
}

.diamonds-amount {
  font-size: 36px;
  font-weight: bold;
  color: #00bfff;
}

.diamonds-label {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
}

.claim-message {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 20px;
}

.claim-btn {
  background: linear-gradient(135deg, #28a745, #20c997) !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 14px 32px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #ffffff !important;
  transition: all 0.3s ease !important;
  width: 100%;
  max-width: 200px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(40, 167, 69, 0.5);
  }

  &:disabled {
    opacity: 0.7 !important;
    cursor: not-allowed !important;
  }
}

// Success State
.success-state {
  position: relative;
  padding: 20px 0;
}

.confetti-container {
  position: absolute;
  top: -100px;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
}

.confetti {
  position: absolute;
  top: -10px;
  border-radius: 2px;
  animation: confetti-fall linear forwards;
}

.success-content {
  position: relative;
  z-index: 1;
}

.success-icon {
  font-size: 72px;
  margin-bottom: 16px;
  animation: bounce 0.6s ease;

  @media (min-width: 768px) {
    font-size: 80px;
  }
}

.success-title {
  font-size: 28px;
  font-weight: bold;
  color: #28a745;
  margin: 0 0 12px 0;

  @media (min-width: 768px) {
    font-size: 32px;
  }
}

.success-message {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 16px;

  strong {
    color: #00bfff;
  }
}

.success-diamond-badge {
  display: inline-block;
  padding: 12px 24px;
  background: linear-gradient(135deg, #00bfff, #00d4ff);
  color: #1a1a2e;
  font-size: 20px;
  font-weight: bold;
  border-radius: 30px;
  animation: scale-in 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

// Animations
@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

@keyframes pulse-glow {
  0%,
  100% {
    box-shadow: 0 0 10px rgba(0, 191, 255, 0.2);
  }
  50% {
    box-shadow: 0 0 25px rgba(0, 191, 255, 0.4);
  }
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes scale-in {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes confetti-fall {
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(500px) rotate(720deg);
    opacity: 0;
  }
}

// Transitions
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .free-diamond-modal-content,
.modal-fade-leave-active .free-diamond-modal-content {
  transition: transform 0.3s ease;
}

.modal-fade-enter .free-diamond-modal-content,
.modal-fade-leave-to .free-diamond-modal-content {
  transform: scale(0.9);
}

.scale-fade-enter-active,
.scale-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.scale-fade-enter,
.scale-fade-leave-to {
  transform: scale(0.8);
  opacity: 0;
}
</style>
